import React, { useState, useEffect } from 'react';
import { batchAPI, adminAPI } from '@/api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, Plus, Edit, Trash2, Download, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import StudentProfile from './StudentProfile';
import { useAuth } from '@/contexts/AuthContext';

const BatchManagement = ({ userRole }) => {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkRegister, setShowBulkRegister] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  // Edit batch modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [editFormData, setEditFormData] = useState({
    validity_months: 5,
    description: ''
  });
  
  // View batch students
  const [showBatchStudents, setShowBatchStudents] = useState(false);
  const [batchStudents, setBatchStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  // Student profile in batch
  const [showStudentProfile, setShowStudentProfile] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Extend single student validity
  const [showExtendValidity, setShowExtendValidity] = useState(false);
  const [extendStudent, setExtendStudent] = useState(null);
  const [extendMonths, setExtendMonths] = useState(1);
  const [extending, setExtending] = useState(false);
  
  const canExtendValidity = user?.role === 'admin' || user?.can_manage_batch_validity;
  
  const formatValidity = (value) => {
    if (!value) return 'Not set';
    const d = new Date(value);
    if (isNaN(d.getTime())) return 'Not set';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  
  const isExpired = (value) => {
    if (!value) return false;
    const d = new Date(value);
    return !isNaN(d.getTime()) && d < new Date();
  };
  
  const handleExtendValidity = async () => {
    if (!extendStudent) return;
    setExtending(true);
    try {
      const response = await batchAPI.extendStudentValidity(extendStudent.id, parseInt(extendMonths));
      const newDate = formatValidity(response.data.valid_until);
      toast.success(`${extendStudent.name}-এর মেয়াদ বাড়ানো হয়েছে: ${newDate}`);
      if (!response.data.email_sent) {
        toast.warning('Validity updated, but the notification email could not be sent');
      }
      setBatchStudents((prev) => prev.map((s) => (
        s.id === extendStudent.id
          ? { ...s, valid_until: response.data.valid_until, validity_override: true }
          : s
      )));
      setShowExtendValidity(false);
      setExtendStudent(null);
      setExtendMonths(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend validity');
    } finally {
      setExtending(false);
    }
  };
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    listening_credits: 6,
    reading_credits: 6,
    writing_credits: 6,
    speaking_credits: 6,
    mock_credits: 3,
    validity_months: 5,  // Default 5 months
  });

  const [bulkStudents, setBulkStudents] = useState('');

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const response = await batchAPI.getList();
      setBatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBatch = async () => {
    try {
      await batchAPI.create(formData);
      toast.success('Batch created successfully!');
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        listening_credits: 6,
        reading_credits: 6,
        writing_credits: 6,
        speaking_credits: 6,
        mock_credits: 3,
        validity_months: 5,
      });
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create batch');
    }
  };

  const handleBulkRegister = async () => {
    if (!selectedBatch) return;
    
    try {
      // Parse CSV format: email,name,phone,password,exam_category
      const lines = bulkStudents.trim().split('\n');
      const students = lines.map(line => {
        const [email, name, phone, password, exam_category] = line.split(',').map(s => s.trim());
        return { 
          email, 
          name, 
          phone: phone || '', 
          password: password || 'student123',
          exam_category: exam_category || 'PB'  // Default to PB
        };
      });

      const response = await batchAPI.bulkRegister({
        batch_id: selectedBatch.id,
        students
      });

      toast.success(`${response.data.success_count} students registered successfully!`);
      
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} students failed to register`);
      }

      setShowBulkRegister(false);
      setBulkStudents('');
      fetchBatches();
    } catch (error) {
      toast.error('Bulk registration failed');
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) return;

    try {
      await batchAPI.delete(batchId);
      toast.success('Batch deleted successfully!');
      fetchBatches();
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  };
  
  const handleEditBatch = (batch) => {
    setEditingBatch(batch);
    setEditFormData({
      validity_months: batch.validity_months || 5,
      description: batch.description || ''
    });
    setShowEditModal(true);
  };
  
  const handleUpdateBatch = async () => {
    try {
      await batchAPI.update(editingBatch.id, editFormData);
      toast.success(`Batch "${editingBatch.name}" updated successfully! Student validity periods have been updated.`);
      setShowEditModal(false);
      setEditingBatch(null);
      fetchBatches();
    } catch (error) {
      toast.error('Failed to update batch');
    }
  };
  
  const handleViewBatchStudents = async (batch) => {
    setSelectedBatch(batch);
    setLoadingStudents(true);
    setShowBatchStudents(true);
    
    try {
      const response = await batchAPI.getStudents(batch.id);
      setBatchStudents(response.data);
    } catch (error) {
      toast.error('Failed to fetch batch students');
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleDownloadBatchCSV = async (batchName) => {
    try {
      const response = await adminAPI.exportBatchCSV(batchName);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `batch_${batchName}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV downloaded successfully!');
    } catch (error) {
      console.error('Failed to download CSV:', error);
      toast.error('Failed to download CSV');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Batch Management</CardTitle>
              <CardDescription>Create and manage student batches with credit configurations</CardDescription>
            </div>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Batch
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Batches List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {batches.map((batch) => (
          <Card key={batch.id} className="backdrop-blur-lg bg-white/80 shadow-lg border-0 hover:shadow-xl transition-shadow">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold text-blue-600">{batch.name}</h3>
                    <p className="text-sm text-gray-600">{batch.description}</p>
                  </div>
                  <Badge 
                    className="bg-green-600 cursor-pointer hover:bg-green-700 transition-colors" 
                    onClick={() => handleViewBatchStudents(batch)}
                    title="Click to view students"
                  >
                    <Users className="w-3 h-3 mr-1" />
                    {batch.total_students || 0}
                  </Badge>
                </div>

                <div className="border-t pt-3 space-y-2">
                  <div className="text-sm font-semibold text-gray-700">Credit Configuration:</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span>Listening:</span>
                      <Badge variant="outline">{batch.listening_credits}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Reading:</span>
                      <Badge variant="outline">{batch.reading_credits}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Writing:</span>
                      <Badge variant="outline">{batch.writing_credits}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Speaking:</span>
                      <Badge variant="outline">{batch.speaking_credits}</Badge>
                    </div>
                    <div className="flex justify-between col-span-2">
                      <span className="font-semibold">Mock:</span>
                      <Badge variant="outline" className="bg-purple-50">{batch.mock_credits}</Badge>
                    </div>
                  </div>
                </div>
                
                {/* Validity Info */}
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>Validity: <strong>{batch.validity_months || 5} months</strong></span>
                      <span className="text-gray-500">
                        (Till {(() => {
                          const baseDate = batch.created_at ? new Date(batch.created_at) : new Date();
                          const endDate = new Date(baseDate);
                          endDate.setMonth(endDate.getMonth() + (batch.validity_months || 5));
                          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                                             'July', 'August', 'September', 'October', 'November', 'December'];
                          
                          return `${endDate.getDate()} ${monthNames[endDate.getMonth()]}, ${endDate.getFullYear()}`;
                        })()})
                      </span>
                    </div>
                    {/* Show Edit button only for Admin or Moderator with can_manage_batch_validity permission */}
                    {(user?.role === 'admin' || user?.can_manage_batch_validity) && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        onClick={() => handleEditBatch(batch)}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setSelectedBatch(batch);
                      setShowBulkRegister(true);
                    }}
                    className="flex-1"
                  >
                    Add Students
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleDownloadBatchCSV(batch.name)}
                    title="Download CSV"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  {userRole === 'admin' && (
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDeleteBatch(batch.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Batch Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Batch</DialogTitle>
            <DialogDescription>Set up a new batch with credit configuration</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Batch Name *</Label>
              <Input
                placeholder="e.g., RICU129"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="Optional description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="border-t pt-3">
              <div className="font-semibold mb-3">Credit Configuration per Student:</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Listening Credits</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.listening_credits}
                    onChange={(e) => setFormData({ ...formData, listening_credits: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Reading Credits</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.reading_credits}
                    onChange={(e) => setFormData({ ...formData, reading_credits: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Writing Credits</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.writing_credits}
                    onChange={(e) => setFormData({ ...formData, writing_credits: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Speaking Credits</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.speaking_credits}
                    onChange={(e) => setFormData({ ...formData, speaking_credits: parseInt(e.target.value) })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Mock Test Credits</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.mock_credits}
                    onChange={(e) => setFormData({ ...formData, mock_credits: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            </div>

            {/* Validity Period */}
            <div className="space-y-2">
              <Label htmlFor="validity_months">
                Validity Period (Months) 
                <span className="text-sm text-gray-500 ml-2">
                  Default: 5 months from student creation
                </span>
              </Label>
              <Input
                id="validity_months"
                type="number"
                min="1"
                max="24"
                value={formData.validity_months}
                onChange={(e) => setFormData({ ...formData, validity_months: parseInt(e.target.value) })}
                placeholder="e.g., 5"
              />
              <p className="text-xs text-gray-500">
                Students will have access until {formData.validity_months} month(s) from batch creation date
              </p>
            </div>

            <Button 
              onClick={handleCreateBatch}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600"
            >
              Create Batch
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Batch Students Modal */}
      <Dialog open={showBatchStudents} onOpenChange={setShowBatchStudents}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Students in {selectedBatch?.name}</DialogTitle>
            <DialogDescription>
              View and manage students in this batch
            </DialogDescription>
          </DialogHeader>
          
          {loadingStudents ? (
            <div className="text-center py-8">Loading students...</div>
          ) : batchStudents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No students in this batch yet
            </div>
          ) : (
            <div className="space-y-3">
              {batchStudents.map((student) => (
                <div 
                  key={student.id}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex-1">
                    <div className="font-semibold text-lg">{student.name}</div>
                    <div className="text-sm text-gray-600">{student.email}</div>
                    {student.phone && (
                      <div className="text-sm text-gray-500">📱 {student.phone}</div>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span
                        className={`text-xs font-medium inline-flex items-center gap-1 ${isExpired(student.valid_until) ? 'text-red-600' : 'text-gray-600'}`}
                        data-testid={`student-validity-${student.id}`}
                      >
                        <Calendar className="w-3 h-3" />
                        Valid till: {formatValidity(student.valid_until)}
                      </span>
                      {student.validity_override && (
                        <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-50">
                          Custom
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Credits</div>
                      <div className="text-xl font-bold text-blue-600">
                        {student.credit_balance || 0}
                      </div>
                    </div>
                    {canExtendValidity && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid={`extend-validity-btn-${student.id}`}
                        onClick={() => {
                          setExtendStudent(student);
                          setExtendMonths(1);
                          setShowExtendValidity(true);
                        }}
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        Extend
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline"
                      data-testid={`edit-student-btn-${student.id}`}
                      onClick={() => {
                        setSelectedStudent(student);
                        setShowStudentProfile(true);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Extend Single Student Validity Modal */}
      <Dialog open={showExtendValidity} onOpenChange={setShowExtendValidity}>
        <DialogContent className="max-w-md" data-testid="extend-validity-modal">
          <DialogHeader>
            <DialogTitle>Extend Validity</DialogTitle>
            <DialogDescription>
              Extend validity for this student only — other students in the batch stay unchanged
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="font-semibold">{extendStudent?.name}</div>
              <div className="text-sm text-gray-600">{extendStudent?.email}</div>
              <div className="text-sm text-gray-700 mt-1">
                Current validity: <strong>{formatValidity(extendStudent?.valid_until)}</strong>
                {isExpired(extendStudent?.valid_until) && (
                  <span className="text-red-600 ml-1">(expired)</span>
                )}
              </div>
            </div>
            
            <div>
              <Label htmlFor="extend-months">Extend by (months)</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {[1, 2, 3, 6].map((m) => (
                  <Button
                    key={m}
                    type="button"
                    size="sm"
                    variant={parseInt(extendMonths) === m ? 'default' : 'outline'}
                    data-testid={`extend-preset-${m}`}
                    onClick={() => setExtendMonths(m)}
                  >
                    +{m}
                  </Button>
                ))}
              </div>
              <Input
                id="extend-months"
                type="number"
                min="1"
                max="60"
                className="mt-2"
                data-testid="extend-months-input"
                value={extendMonths}
                onChange={(e) => setExtendMonths(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-2">
                {isExpired(extendStudent?.valid_until)
                  ? 'Account is expired, so the new date is counted from today.'
                  : 'Added on top of the current validity date.'}
                {' '}The student will get an email notification.
              </p>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowExtendValidity(false)} data-testid="extend-validity-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleExtendValidity}
                disabled={extending || !extendMonths || parseInt(extendMonths) < 1}
                data-testid="extend-validity-confirm"
              >
                {extending ? 'Extending...' : 'Extend Validity'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Register Modal */}
      <Dialog open={showBulkRegister} onOpenChange={setShowBulkRegister}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Register Students - {selectedBatch?.name}</DialogTitle>
            <DialogDescription>
              Enter student details in CSV format (one per line)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Student Data (CSV Format)</Label>
              <div className="text-xs text-gray-500 mb-2">
                Format: email,name,phone,password,exam_category (one student per line)
                <br />
                exam_category: PB (Paper Based) or CD (Computer Delivered) - Default: PB if omitted
              </div>
              <textarea
                className="w-full h-64 p-3 border rounded-md font-mono text-sm"
                placeholder="student1@example.com,John Doe,01712345678,pass123,PB&#10;student2@example.com,Jane Smith,01723456789,pass456,CD"
                value={bulkStudents}
                onChange={(e) => setBulkStudents(e.target.value)}
              />
            </div>

            <div className="bg-blue-50 p-3 rounded-md text-sm">
              <div className="font-semibold mb-2">Credit allocation per student:</div>
              <div className="grid grid-cols-2 gap-2">
                <div>Listening: {selectedBatch?.listening_credits || 0}</div>
                <div>Reading: {selectedBatch?.reading_credits || 0}</div>
                <div>Writing: {selectedBatch?.writing_credits || 0}</div>
                <div>Speaking: {selectedBatch?.speaking_credits || 0}</div>
                <div>Mock: {selectedBatch?.mock_credits || 0}</div>
                <div className="font-bold">
                  Total: {(selectedBatch?.listening_credits || 0) + 
                           (selectedBatch?.reading_credits || 0) + 
                           (selectedBatch?.writing_credits || 0) + 
                           (selectedBatch?.speaking_credits || 0) + 
                           (selectedBatch?.mock_credits || 0)}
                </div>
              </div>
            </div>

            <Button 
              onClick={handleBulkRegister}
              className="w-full bg-gradient-to-r from-green-600 to-blue-600"
            >
              Register Students
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Batch Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Batch: {editingBatch?.name}
            </DialogTitle>
            <DialogDescription>
              Update batch validity period. All students in this batch will have their validity updated.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_validity">Validity Period (Months)</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="edit_validity"
                  type="number"
                  min="1"
                  max="24"
                  value={editFormData.validity_months}
                  onChange={(e) => setEditFormData({ ...editFormData, validity_months: parseInt(e.target.value) || 5 })}
                  className="w-24"
                />
                <span className="text-sm text-gray-500">months from account creation</span>
              </div>
              <p className="text-xs text-gray-500">
                Current: {editingBatch?.validity_months || 5} months
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_description">Description (Optional)</Label>
              <Input
                id="edit_description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                placeholder="Batch description..."
              />
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">What happens when you update validity?</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• All students in this batch will have their validity extended</li>
                    <li>• Students can continue to login until the new validity date</li>
                    <li>• Email notifications will be sent before expiration</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateBatch}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Update Batch
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Student Profile Modal */}
      {showStudentProfile && selectedStudent && (
        <StudentProfile
          student={selectedStudent}
          onClose={() => {
            setShowStudentProfile(false);
            setSelectedStudent(null);
          }}
          onUpdate={() => {
            // Refresh batch students list
            if (selectedBatch) {
              handleViewBatchStudents(selectedBatch);
            }
            setShowStudentProfile(false);
            setSelectedStudent(null);
          }}
        />
      )}
    </div>
  );
};

export default BatchManagement;
